/**
 * CapacityBar Component
 * Visual indicator of spot capacity utilization and filter breakdown
 * Inspired by Claude Code's context usage visualization
 */

import { Box, Typography, useTheme } from "@mui/material";
import React from "react";
import { FilterBreakdown } from "../../TemporalTest/types";

interface CapacityBarProps {
  /** Total spots on the ramp */
  totalSpots: number;
  /** Number of aircraft successfully placed */
  placedCount: number;
  /** Number of spots available after filters (for at least one aircraft) */
  availableCount: number;
  /** Detailed breakdown of filter eliminations (optional, aggregated from all rejected aircraft) */
  filterBreakdown?: FilterBreakdown;
}

/**
 * CapacityBar displays a visual bar showing spot utilization:
 * - Placed spots (green)
 * - Available spots (blue)
 * - Filtered spots (grey)
 *
 * Also shows a legend with detailed breakdown by filter type
 */
export const CapacityBar: React.FC<CapacityBarProps> = React.memo(
  ({ totalSpots, placedCount, availableCount, filterBreakdown }) => {
    const theme = useTheme();

    // Calculate counts
    const filteredCount = totalSpots - placedCount - availableCount;
    const placedPercentage =
      totalSpots > 0 ? (placedCount / totalSpots) * 100 : 0;

    // Calculate proportions for visual bar (in percentages)
    const placedWidth = totalSpots > 0 ? (placedCount / totalSpots) * 100 : 0;
    const availableWidth =
      totalSpots > 0 ? (availableCount / totalSpots) * 100 : 0;
    const filteredWidth =
      totalSpots > 0 ? (filteredCount / totalSpots) * 100 : 0;

    // Color palette
    const colors = {
      placed: theme.palette.success.main,
      available: theme.palette.primary.main,
      filtered: theme.palette.grey[400],
      time: theme.palette.error.main,
      size: theme.palette.warning.main,
      duration: theme.palette.warning.light,
      disabled: theme.palette.grey[500],
      geometry: theme.palette.secondary.main,
    };

    return (
      <Box
        sx={{
          mb: 2,
          p: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "background.paper",
        }}
      >
        {/* Title and Summary */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            Spot Capacity
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "text.secondary" }}
          >
            {placedCount}/{totalSpots} spots ({placedPercentage.toFixed(0)}%
            utilized)
          </Typography>
        </Box>

        {/* Visual Bar */}
        <Box
          sx={{
            display: "flex",
            height: "24px",
            width: "100%",
            borderRadius: "4px",
            overflow: "hidden",
            bgcolor: theme.palette.grey[200],
            mb: 1,
          }}
        >
          {/* Placed segment */}
          {placedWidth > 0 && (
            <Box
              sx={{
                width: `${placedWidth}%`,
                bgcolor: colors.placed,
                transition: "width 0.3s ease",
              }}
              title={`${placedCount} placed`}
            />
          )}
          {/* Available segment */}
          {availableWidth > 0 && (
            <Box
              sx={{
                width: `${availableWidth}%`,
                bgcolor: colors.available,
                transition: "width 0.3s ease",
              }}
              title={`${availableCount} available`}
            />
          )}
          {/* Filtered segment */}
          {filteredWidth > 0 && (
            <Box
              sx={{
                width: `${filteredWidth}%`,
                bgcolor: colors.filtered,
                transition: "width 0.3s ease",
              }}
              title={`${filteredCount} filtered`}
            />
          )}
        </Box>

        {/* Legend */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            fontSize: "0.875rem",
          }}
        >
          {/* Placed */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: colors.placed,
                borderRadius: "2px",
              }}
            />
            <Typography variant="body2">
              Placed <strong>({placedCount})</strong>
            </Typography>
          </Box>

          {/* Available */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: colors.available,
                borderRadius: "2px",
              }}
            />
            <Typography variant="body2">
              Available <strong>({availableCount})</strong>
            </Typography>
          </Box>

          {/* Filtered (with breakdown if available) */}
          {filteredCount > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: colors.filtered,
                  borderRadius: "2px",
                }}
              />
              <Typography variant="body2">
                Filtered <strong>({filteredCount})</strong>
                {filterBreakdown &&
                  (filterBreakdown.timeConflict > 0 ||
                    filterBreakdown.sizeIncompatible > 0 ||
                    filterBreakdown.durationExceeded > 0 ||
                    filterBreakdown.disabled > 0 ||
                    filterBreakdown.geometryCollision > 0) && (
                    <span style={{ color: theme.palette.text.secondary }}>
                      :{" "}
                      {[
                        filterBreakdown.timeConflict > 0 &&
                          `Time (${filterBreakdown.timeConflict})`,
                        filterBreakdown.sizeIncompatible > 0 &&
                          `Size (${filterBreakdown.sizeIncompatible})`,
                        filterBreakdown.durationExceeded > 0 &&
                          `Duration (${filterBreakdown.durationExceeded})`,
                        filterBreakdown.disabled > 0 &&
                          `Disabled (${filterBreakdown.disabled})`,
                        filterBreakdown.geometryCollision > 0 &&
                          `Geometry (${filterBreakdown.geometryCollision})`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  )}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

CapacityBar.displayName = "CapacityBar";
